import { getState } from "storeAccess"
import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import { uploadDocumentFile } from "./limsForskrifterUpload"

// Forskrifter — controlled method documents (PRD-003 Area 2 / SPEC-008; SPEC-021 cutover:
// persisted via the REAL /lims/forskrifter endpoints). A "forskrift" is the written method a lab
// follows, uploaded as Word or PDF, linked to one or more analysis definitions, and carried
// through a two-step approval workflow — Reviewer approval, then SUPERVISOR approval — before a
// version becomes EFFECTIVE. (SPEC-039 / F7: the 2nd approver moved from QA to SUPERVISOR so all
// stamdata change-control uses one reviewer→supervisor pattern; QA is reserved for batch release.
// The server renamed the column qa_signature_id → supervisor_signature_id; historical signatures
// stay attested and now arrive as `supervisorSignature`.)
//
// The SERVER owns the document/version state machine (SP-E Phase 2.6) — the FE never PATCHes
// version blobs (a versions-PATCH would have cleared the definition links; audit finding):
//
//   POST  /lims/forskrifter                      {input:{name, analysisDefinitionIds,
//                                                 file:{key,name,contentType,size}}} → {item}
//   POST  /lims/forskrifter/<id>/versions        {input:{file:{...}}} → {item}
//   POST  /lims/forskrifter/<id>/versions/<vid>/reviewer-approve  {password, reason?} → {item}
//         (draft → reviewer-approved; "reviewer" capability, 4-eyes ≠ version creator)
//   POST  /lims/forskrifter/<id>/versions/<vid>/supervisor-approve {password, reason?} → {item}
//         (reviewer-approved → effective, prior effective → superseded; "supervisor" capability,
//          4-eyes ≠ creator + reviewer signer)  (SPEC-039 / F7: was qa-approve / "qa")
//   PATCH /lims/forskrifter/<id>                 {patch:{analysisDefinitionIds}} → {item}
//         (RELINK ONLY — replaces the m2m link set; nothing else is patchable)
//
// File bytes go through the presigned-POST seam (limsForskrifterUpload; the server mints the
// team-scoped key). Every mutation is audited server-side. No hard deletes (GMP retention).
//
// Data shape (one DOCUMENT record, server dump):
//   { id, name, analysisDefinitionIds: [defId, ...], createdAt, updatedAt,
//     versions: [ { id, versionNumber, file: {key,name,contentType,size},
//                   state: "draft"|"reviewer-approved"|"effective"|"superseded",
//                   createdBy, createdAt,
//                   reviewerSignature|null, supervisorSignature|null }, ... ] }   // oldest-first

export const FORSKRIFT_STATES = {
  DRAFT: "draft",
  REVIEWER_APPROVED: "reviewer-approved",
  EFFECTIVE: "effective",
  SUPERSEDED: "superseded",
}

const initialState = { items: [], isLoading: false }

function sortByName(items) {
  return [...items].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
}

// Versions are kept oldest-first (ascending versionNumber) so the history reads top-to-bottom
// and the "latest" is the last element.
function normalizeDoc(doc) {
  const versions = Array.isArray(doc.versions) ? [...doc.versions] : []
  versions.sort((a, b) => (a.versionNumber || 0) - (b.versionNumber || 0))
  return {
    id: doc.id,
    name: doc.name || "",
    analysisDefinitionIds: Array.isArray(doc.analysisDefinitionIds)
      ? doc.analysisDefinitionIds.filter((x) => typeof x === "string" && x)
      : [],
    versions,
    createdAt: doc.createdAt || null,
    updatedAt: doc.updatedAt || null,
  }
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsForskrifter/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsForskrifter/fetch":
      return { items: sortByName(action.res.map(normalizeDoc)), isLoading: false }
    case "limsForskrifter/upsert":
      return {
        ...state,
        items: sortByName(
          state.items.some((x) => x.id === action.res.id)
            ? state.items.map((x) => (x.id === action.res.id ? normalizeDoc(action.res) : x))
            : [...state.items, normalizeDoc(action.res)]
        ),
      }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsForskrifter/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/forskrifter").then((res) => res.items)
})

// Create a new forskrift document: upload the file (presigned-POST seam; the server mints the
// team-scoped key) then create the document — the server builds the first draft version
// (versionNumber 1) and audits. `meta` is { name, analysisDefinitionIds }.
export const createDocument = asyncAction(
  "limsForskrifter/upsert",
  (_dispatch, meta, file, { onProgress } = {}) => {
    return uploadDocumentFile(file, { onProgress }).then((descriptor) => {
      const input = {
        name: (meta && meta.name) || "",
        analysisDefinitionIds:
          meta && Array.isArray(meta.analysisDefinitionIds)
            ? meta.analysisDefinitionIds.filter((x) => typeof x === "string" && x)
            : [],
        file: descriptor,
      }
      return limsRequest("POST", "/lims/forskrifter", { input }).then((res) => res.item)
    })
  }
)

// Add a new version to an existing document: upload the file, then POST the versions sub-route —
// the server appends a draft version with the next versionNumber (prior versions untouched).
export const addVersion = asyncAction(
  "limsForskrifter/upsert",
  (_dispatch, docId, file, { onProgress } = {}) => {
    return uploadDocumentFile(file, { onProgress }).then((descriptor) =>
      limsRequest("POST", `/lims/forskrifter/${docId}/versions`, { input: { file: descriptor } }).then(
        (res) => res.item
      )
    )
  }
)

// Resolve a short-lived presigned GET url for a version's stored file key (the read-side seam
// the FE swaps to now that SP-E ships the download contract — SPEC-021 B6). Not an asyncAction:
// it touches no store state, just returns the url so callers open/preview it on demand (the url
// expires in ~1h, so resolving it lazily — never caching it in redux — is the point).
//
// Circuit breaker (RR7 verify follow-up #3): when LIMS_FORSKRIFTER_BUCKET is not provisioned the
// server answers EVERY call with a clean 500. ForskriftViewer resolves a url on each mount / file
// change and the register opens one per click, so across a session this becomes a request +
// console-error storm that saturates the network. After a run of consecutive failures we OPEN the
// breaker and short-circuit further calls for a cooldown; every caller already treats a rejected
// url as "unavailable" (disabled open/download, or an error toast), so the UI degrades gracefully
// instead of hammering a permanently-failing endpoint. Any success closes the breaker, and it
// re-probes once per cooldown — so it self-heals the moment the bucket is provisioned. Module-local
// (per page load) by design, matching the contract that read urls are never cached in redux.
const READ_URL_FAIL_THRESHOLD = 3
const READ_URL_COOLDOWN_MS = 60000
let readUrlConsecutiveFailures = 0
let readUrlBlockedUntil = 0

export const getReadUrl = (key) => () => {
  if (readUrlBlockedUntil && Date.now() < readUrlBlockedUntil) {
    return Promise.reject(new Error("LIMS document storage unavailable — read-url temporarily disabled"))
  }
  return limsRequest("POST", "/lims/forskrifter/get-read-url", { input: { key } })
    .then((res) => {
      readUrlConsecutiveFailures = 0
      readUrlBlockedUntil = 0
      return res.presignedUrl
    })
    .catch((err) => {
      readUrlConsecutiveFailures += 1
      if (readUrlConsecutiveFailures >= READ_URL_FAIL_THRESHOLD) {
        readUrlBlockedUntil = Date.now() + READ_URL_COOLDOWN_MS
      }
      throw err
    })
}

// Reviewer-approves a draft version: draft -> reviewer-approved. The signature's password +
// reason ride at the request-body TOP LEVEL; the server enforces the "reviewer" capability and
// 4-eyes (≠ the version creator) and stamps the immutable signature.
export const reviewerApprove = asyncAction(
  "limsForskrifter/upsert",
  (_dispatch, docId, versionId, signature) => {
    return limsRequest("POST", `/lims/forskrifter/${docId}/versions/${versionId}/reviewer-approve`, {
      password: (signature && signature.password) || undefined,
      reason: (signature && signature.reason) || undefined,
    }).then((res) => res.item)
  }
)

// Supervisor-approves a reviewer-approved version: -> effective; the server supersedes any other
// effective version of the same document in the same transaction. "supervisor" capability, 4-eyes
// (≠ the version creator AND the reviewer signer) — all enforced server-side. (SPEC-039 / F7: was
// qaApprove / the qa-approve endpoint / "qa" capability.)
export const supervisorApprove = asyncAction(
  "limsForskrifter/upsert",
  (_dispatch, docId, versionId, signature) => {
    return limsRequest("POST", `/lims/forskrifter/${docId}/versions/${versionId}/supervisor-approve`, {
      password: (signature && signature.password) || undefined,
      reason: (signature && signature.reason) || undefined,
    }).then((res) => res.item)
  }
)

// Replace the document's analysis-definition link set (many-to-many) — the ONLY thing the
// PATCH endpoint accepts. A no-op when the ids are unchanged (avoids a spurious round-trip;
// the server also no-ops + skips the audit on an unchanged set).
export const linkDefinitions = asyncAction(
  "limsForskrifter/upsert",
  (_dispatch, docId, analysisDefinitionIds) => {
    const doc = selectDocumentById(getState(), docId)
    if (!doc) return Promise.reject(new Error("linkDefinitions: document not found"))
    const next = Array.isArray(analysisDefinitionIds)
      ? analysisDefinitionIds.filter((x) => typeof x === "string" && x)
      : []
    const prev = doc.analysisDefinitionIds || []
    const unchanged = next.length === prev.length && next.every((id) => prev.includes(id))
    if (unchanged) return Promise.resolve(doc)
    return limsRequest("PATCH", `/lims/forskrifter/${docId}`, {
      patch: { analysisDefinitionIds: next },
    }).then((res) => res.item)
  }
)

// Persist this analysis definition's forskrift links: link `definitionId` onto every doc in
// `wantedDocIds` and unlink it from every doc that previously carried it but is now deselected.
// This is the definition-side write the AnalysisConfigurationPage chains after the definition
// save. Returns a promise resolving once all affected docs are written.
export const setLinksForDefinition = (definitionId, wantedDocIds) => (dispatch) => {
  if (!definitionId) return Promise.resolve()
  const wanted = new Set((wantedDocIds || []).filter(Boolean))
  const docs = selectDocuments(getState())
  const updates = []
  docs.forEach((doc) => {
    const has = (doc.analysisDefinitionIds || []).includes(definitionId)
    const want = wanted.has(doc.id)
    if (has === want) return
    const nextIds = want
      ? [...doc.analysisDefinitionIds, definitionId]
      : doc.analysisDefinitionIds.filter((id) => id !== definitionId)
    updates.push(dispatch(linkDefinitions(doc.id, nextIds)))
  })
  return Promise.all(updates)
}

// --- selectors ---

export const selectDocuments = (state) => state.limsForskrifter.items

export const selectDocumentById = (state, docId) =>
  state.limsForskrifter.items.find((d) => d.id === docId) || null

// Every document linked (many-to-many) to the analysis definition `defId`, name-sorted. Powers
// the analysis-first register (SPEC-014): each analysis row expands to the documents attached to
// it. Returns [] when `defId` is falsy or no document links it. NB: returns a fresh array — call
// sites should select per defId (one row at a time) or shallow-compare to avoid extra renders.
export const selectDocumentsForDefinition = (state, defId) => {
  if (!defId) return []
  return state.limsForskrifter.items.filter((d) =>
    (d.analysisDefinitionIds || []).includes(defId)
  )
}

export const selectVersions = (state, docId) => {
  const doc = selectDocumentById(state, docId)
  return doc ? doc.versions : []
}

// The single effective version of the document(s) linked to `defId`. Returns the first
// effective version found across the linked documents (one effective per document; if several
// documents govern the same definition, the most recently updated wins) or null. Carries the
// owning document name on the result for display.
export const selectEffectiveForDefinition = (state, defId) => {
  if (!defId) return null
  const linked = state.limsForskrifter.items
    .filter((d) => (d.analysisDefinitionIds || []).includes(defId))
    .sort((a, b) => ((a.updatedAt || "") < (b.updatedAt || "") ? 1 : -1))
  for (const doc of linked) {
    const effective = doc.versions.find((v) => v.state === FORSKRIFT_STATES.EFFECTIVE)
    if (effective) return { document: doc, version: effective }
  }
  return null
}

// The latest version of a document (highest versionNumber) — what the register's primary
// actions target. null for a document with no versions (should not happen post-create).
export const selectLatestVersion = (state, docId) => {
  const versions = selectVersions(state, docId)
  return versions.length ? versions[versions.length - 1] : null
}
